import * as Sentry from "@sentry/react";
import {
  RouterProvider,
  createHashHistory,
  createRouter,
} from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import { createDB } from "./db";
import { elementsQueryOptions } from "./hooks/queries/elementsQuery";
import { profilesQueryOptions } from "./hooks/queries/profilesQuery";
import { noteVaultsQueryOptions } from "./hooks/queries/useGetNoteVaults";
import { todoVaultsQueryOptions } from "./hooks/queries/useGetTodoVaults";
import { userQueryOptions } from "./hooks/queries/userQuery";
import { AnalyticsProvider, initPosthogClient } from "./lib/analytics";
import { queryClient } from "./lib/query";
import { tokenManager } from "./lib/auth/token-manager";
import {
  scrubSentryEvent,
  scrubSentrySpan,
  scrubUrl,
  secretParams,
} from "./lib/auth/scrub";
import { routeTree } from "./routeTree.gen";
import { store } from "./store";

// Fonts are imported here (not via @import in index.css) so Vite resolves the
// woff2 url()s against the packages and emits the files; Tailwind's CSS
// inliner kept the relative ./files/ URLs, which broke fonts in production.
import "@fontsource/lato";
import "@fontsource-variable/montserrat";
import "./index.css";

// Exported so the `Register` module augmentation in vite-env.d.ts resolves
// (`typeof router`) and typed Link/navigate/useSearch work across the app.
export const router = createRouter({
  routeTree,
  history: import.meta.env.MANIFEST_VERSION ? createHashHistory() : undefined,
  // Preload on hover/focus and keep pending UI flicker-free.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  defaultPendingMs: 200,
  defaultPendingMinMs: 500,
});

if (!import.meta.env.DEV) {
  // Sentry 11 collects more than 10 did by default. Keep the old privacy
  // defaults and redact our secret params on top.
  const piiDeny = ["forwarded", "-ip", "remote-", "via", "-user"];

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // RUM: page-load/navigation traces + web vitals, named after the matched
    // route. Default same-origin trace propagation only — the API's CORS does
    // not allow sentry-trace/baggage yet.
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: 0.2,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { deny: piiDeny },
      httpBodies: [],
      urlQueryParams: { deny: [...piiDeny, ...secretParams] },
    },
    beforeSend: scrubSentryEvent,
    beforeSendSpan: scrubSentrySpan,
    beforeBreadcrumb(breadcrumb) {
      for (const field of ["url", "from", "to"]) {
        const value = breadcrumb.data?.[field];
        if (typeof value === "string") {
          breadcrumb.data![field] = scrubUrl(value);
        }
      }
      return breadcrumb;
    },
  });
}

// Warm the query cache with the boot-critical requests before React mounts.
// The provider hooks use these exact keys/fns, so they dedupe against the
// in-flight prefetches instead of re-serializing the waterfall
// (token → user/profile → elements → widget queries). Fire-and-forget.
function prefetchBootQueries() {
  const { sessionKey } = tokenManager.getState();
  if (!sessionKey) {
    return;
  }

  void queryClient.prefetchQuery(userQueryOptions(sessionKey));
  void queryClient.prefetchQuery(profilesQueryOptions());
  void queryClient.prefetchQuery(noteVaultsQueryOptions());
  void queryClient.prefetchQuery(todoVaultsQueryOptions());

  const storedProfileId = localStorage.getItem("unideck.activeProfileId");
  if (storedProfileId) {
    void queryClient.prefetchQuery(elementsQueryOptions(storedProfileId));
  }
}

function start() {
  // One refresh round-trip restores the session; widgets wait for it through
  // the request interceptor instead of the render.
  void tokenManager.bootstrap().then(prefetchBootQueries);
  // IndexedDB opens in the background; consumers await getDB() readiness
  // instead of gating the first render on it.
  void createDB();
  createRoot(document.getElementById("root")!).render(
    <Provider store={store}>
      <AnalyticsProvider>
        <RouterProvider router={router} />
      </AnalyticsProvider>
    </Provider>,
  );

  // Deferred: loads posthog-js off the critical path (idle after first paint).
  initPosthogClient();
}

start();
